// Per-vault cache settings. The "Enable local cache" toggle is
// per-vault (stored inside the vault via the worker). Max size
// and expiration are per-vault, persisted inside the encrypted
// `appConfig.preferences.cache_prefs_by_vault_id` blob via
// cachePrefs.ts. "Clear cache now" calls the worker's cacheClear
// method.
//
// Local-folder vaults (adapter = opfs) render the toggle disabled
// with an explainer: caching a local vault just double-stores the
// same bytes.

import * as React from "react";
import { useEffect, useState } from "react";
import { useVault } from "@/hooks/useVault";
import { useSession } from "@/stores/session";
import { useMasterSession } from "@/stores/masterSession";
import {
  readDevicePrefs,
  writeDevicePrefs,
  type DeviceCachePrefs,
  type CacheExpiration,
} from "@/lib/cachePrefs";

const SIZE_PRESETS: { label: string; bytes: number }[] = [
  { label: "100 MB", bytes: 100 * 1024 * 1024 },
  { label: "500 MB", bytes: 500 * 1024 * 1024 },
  { label: "1 GB", bytes: 1024 * 1024 * 1024 },
  { label: "2 GB", bytes: 2 * 1024 * 1024 * 1024 },
  { label: "5 GB", bytes: 5 * 1024 * 1024 * 1024 },
];

const EXPIRATION_PRESETS: { label: string; value: CacheExpiration }[] = [
  { label: "Never", value: { kind: "never-expire" } },
  { label: "1 day", value: { kind: "expire-after", durationMs: 86_400_000 } },
  {
    label: "1 week",
    value: { kind: "expire-after", durationMs: 7 * 86_400_000 },
  },
  {
    label: "1 month",
    value: { kind: "expire-after", durationMs: 30 * 86_400_000 },
  },
  { label: "Session only", value: { kind: "session-only" } },
];

export function CacheTab() {
  const worker = useVault();
  const session = useSession((s) => s.session);
  const vaultId = session?.vaultId;
  const handle = session?.handle ?? null;

  // Look up the vault entry from the encrypted app-config to get the
  // adapter type. The session itself does not carry adapter info.
  const vaultEntry = useMasterSession((s) =>
    s.master?.appConfig?.vaults.find((v) => v.id === vaultId),
  );
  const isLocalVault = vaultEntry?.adapter.type === "opfs";

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [devicePrefs, setDevicePrefs] = useState<DeviceCachePrefs | null>(
    null,
  );
  const [sizeBytes, setSizeBytes] = useState<number>(0);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!vaultId || handle === null) return;
    let cancelled = false;
    void (async () => {
      try {
        const [en, sz] = await Promise.all([
          worker.getVaultCacheEnabled(handle),
          worker.cacheSizeBytes(handle),
        ]);
        if (cancelled) return;
        setEnabled(en);
        setSizeBytes(sz);
      } catch {
        // Keep the UI in the loading state on error.
      }
    })();
    const initialPrefs = readDevicePrefs(vaultId);
    setDevicePrefs(initialPrefs);
    if (handle !== null) {
      void worker.setDevicePrefs(handle, initialPrefs).catch(() => {
        // Non-fatal; the worker falls back to construction-time
        // defaults (500 MiB, NeverExpire) until the next push.
      });
    }
    return () => {
      cancelled = true;
    };
  }, [vaultId, handle, worker]);

  if (!vaultId) return null;

  const onToggle = async (next: boolean) => {
    if (handle === null) return;
    setEnabled(next);
    try {
      await worker.setVaultCacheEnabled(handle, next);
      if (!next) {
        await worker.cacheClear(handle, vaultId);
        setSizeBytes(0);
      }
    } catch {
      // Roll back UI on failure.
      setEnabled(!next);
    }
  };

  const persistDevicePrefs = async (
    prior: DeviceCachePrefs,
    next: DeviceCachePrefs,
  ): Promise<void> => {
    if (!vaultId || handle === null) return;
    setDevicePrefs(next);
    setSaveError(null);
    try {
      // Persist to the encrypted app-config FIRST. If this fails the
      // master appConfig is left untouched (writeDevicePrefs is
      // atomic — it only mutates the store on success).
      await writeDevicePrefs(worker, vaultId, next);
      // Then push to the worker's in-memory cache config. If this
      // alone fails it's non-fatal; the worker falls back to its
      // construction-time defaults until the next push.
      await worker.setDevicePrefs(handle, next).catch(() => {
        // Non-fatal — see comment above.
      });
    } catch (err) {
      // Roll back the optimistic UI update so the user sees what's
      // actually stored.
      setDevicePrefs(prior);
      setSaveError(
        err instanceof Error ? err.message : "Could not save cache settings",
      );
    }
  };

  const onSizeChange = (bytes: number) => {
    if (!devicePrefs) return;
    const next: DeviceCachePrefs = { ...devicePrefs, maxBytes: bytes };
    void persistDevicePrefs(devicePrefs, next);
  };

  const onExpirationChange = (value: CacheExpiration) => {
    if (!devicePrefs) return;
    const next: DeviceCachePrefs = { ...devicePrefs, expiration: value };
    void persistDevicePrefs(devicePrefs, next);
  };

  const onClearCache = async () => {
    if (handle === null) return;
    if (
      !window.confirm(
        "Clear the local chunk cache? Next views will be slower until it refills.",
      )
    ) {
      return;
    }
    try {
      await worker.cacheClear(handle, vaultId);
      setSizeBytes(0);
    } catch {
      // no-op
    }
  };

  return (
    <div className="max-w-xl space-y-6">
      <h2 className="text-xl font-medium">Cache</h2>

      <Row
        label="Enable local cache"
        hint={
          isLocalVault
            ? "Disabled — this vault is stored on local disk, so a cache would duplicate data."
            : "Stores chunk data encrypted on disk for fast repeat access."
        }
      >
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={enabled ?? false}
            disabled={isLocalVault || enabled === null}
            onChange={(e) => void onToggle(e.target.checked)}
          />
          <span className="text-sm">{enabled ? "On" : "Off"}</span>
        </label>
      </Row>

      <Row
        label="Max size"
        hint="Per-device. The oldest entries are evicted when the cache exceeds this size."
      >
        <select
          value={devicePrefs?.maxBytes ?? SIZE_PRESETS[1].bytes}
          disabled={isLocalVault || !devicePrefs}
          onChange={(e) => onSizeChange(Number(e.target.value))}
          className="rounded-md border border-border bg-white/5 px-3 py-1 text-sm"
        >
          {SIZE_PRESETS.map((p) => (
            <option key={p.bytes} value={p.bytes}>
              {p.label}
            </option>
          ))}
        </select>
      </Row>

      <Row
        label="Expiration"
        hint="Per-device. Cached entries older than this are dropped on access."
      >
        <div className="flex flex-col items-end gap-1">
          <select
            value={expirationToKey(
              devicePrefs?.expiration ?? { kind: "never-expire" },
            )}
            disabled={isLocalVault || !devicePrefs}
            onChange={(e) => {
              const preset = EXPIRATION_PRESETS.find(
                (p) => expirationToKey(p.value) === e.target.value,
              );
              if (preset) onExpirationChange(preset.value);
            }}
            className="rounded-md border border-border bg-white/5 px-3 py-1 text-sm"
          >
            {EXPIRATION_PRESETS.map((p) => (
              <option key={expirationToKey(p.value)} value={expirationToKey(p.value)}>
                {p.label}
              </option>
            ))}
          </select>
          {devicePrefs?.expiration.kind === "session-only" && (
            <div className="text-[11px] text-text-dim">Applies on next unlock.</div>
          )}
        </div>
      </Row>

      <div className="border-t border-border pt-4">
        <div className="mb-2 text-sm">
          Cached:{" "}
          <span className="text-text-dim">{humanBytes(sizeBytes)}</span>
        </div>
        <button
          type="button"
          onClick={() => void onClearCache()}
          disabled={isLocalVault || sizeBytes === 0}
          className="rounded-md border border-border bg-white/5 px-3 py-1 text-sm hover:bg-accent-bg disabled:opacity-50"
        >
          Clear cache now
        </button>
      </div>

      {saveError !== null && (
        <div
          role="alert"
          className="rounded-md border border-danger/40 bg-danger/10 p-3 text-[13px] text-danger"
        >
          Could not save cache settings: {saveError}
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-t border-border py-3 text-sm">
      <div>
        <div>{label}</div>
        <div className="text-[11px] text-text-dim">{hint}</div>
      </div>
      {children}
    </div>
  );
}

function expirationToKey(e: CacheExpiration): string {
  if (e.kind === "expire-after") return `after-${e.durationMs}`;
  return e.kind;
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MiB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GiB`;
}
